/**
 * THE EXAM CREATION AGENT.
 *
 * Four phases, in this order for reasons that are not stylistic:
 *
 *   1. PLAN (or CHANGE PLAN) — what tasks, of what type, worth what, needing what material.
 *   2. MATERIAL               — figures/plots generated and reviewed before layout is written.
 *   3. SOURCE                 — typst document written against accepted material (new or refined).
 *   4. REPAIR                 — compile and validate; feed diagnostics back; at most a few rounds.
 *
 * WHAT THE MODEL IS AND IS NOT FREE TO DO. Layout is its own: any typst it likes — columns, custom
 * blocks, spacing, tables, boxes. What it may not do is put a task on the page without declaring it,
 * because the metadata stream is the only thing that makes the document machine-readable, and a taskType
 * is not recoverable from a printed sheet by any means. So the contract is narrow and load-bearing:
 * every task goes through `#task(...)`, and the template's functions keep their meaning.
 *
 * The task-type catalogue comes from the behaviour registry itself (`TaskTypeDescription` +
 * `taskTypeBehaviours`), so the types this agent may choose from are exactly the ones the OCR, master
 * solution, grading-scheme and grading agents have behaviour for — and each type's own
 * `examCreationPrompt` is passed along (never `structureAgentPrompt`, which is the EXTRACTION structure
 * agent's own — a scanned exam has no typst to write, so its guidance and this agent's inevitably diverge).
 */

import { logger } from '@adornis/base/logging.js';
import { jsonrepair } from 'jsonrepair';
import {
  examCreationModel,
  examCreationReasoningEffort,
  examCreationTemperature,
  examRepairTemperature,
} from '../../../shared/models.js';
import { addCostToExam } from '../add-cost.js';
import { getLLMRouter } from '../llm/llm-gate.js';
import { TaskType, TaskTypeDescription, taskTypeBehaviours } from '../task-types/task-main.js';
import {
  compileAndPersistExam,
  compileExamSource,
  compileStandaloneSvg,
  readTypstFile,
  TypstCompileError,
  type ExamAsset,
  type PersistResult,
} from './compile-exam.js';
import type { AnswerType } from './exam-structure.js';
import {
  extensionForImage,
  meetsCalloutMinimum,
  prepareExamImages,
  type AcceptedImage,
  type Callout,
  type FigureMode,
  type FigureText,
  type ImageOutcome,
  type ImageRequest,
} from './exam-images.js';
import { preparePlots, type AcceptedPlot, type PlotOutcome, type PlotRequest } from './exam-plots.js';
import {
  ExamCreationPhase,
  setExamCreationFinishedByExam,
  setExamCreationPdfPersistedByExam,
  setExamCreationPhaseByExam,
} from '../../confirmation-progress.js';
import { formatTypstString } from './exam-document.js';

/** How many times a source that does not compile or does not validate may be sent back for repair. */
const MAX_REPAIR_ROUNDS = 3;

/**
 * How many times a PLAN with an unfixable-by-the-writer defect may be sent back before this deterministic
 * fallback demotes it instead. Lower than `MAX_REPAIR_ROUNDS`: this round-trips one JSON object, not a
 * whole document, so it is cheap enough to try but there is no reason it should need as many attempts.
 */
const MAX_PLAN_REPAIR_ROUNDS = 2;

// ─── interfaces ───────────────────────────────────────────────────────────────────────────────────

export interface ExamCreationRequest {
  subject?: string;
  /** School class or course, e.g. "10b". */
  schoolClass?: string;
  topic?: string;
  durationMinutes?: number;
  totalPoints?: number;
  /** ISO or already-formatted; printed as given. */
  date?: string;
  teacher?: string;
  school?: string;
  /** The exam's own language, which is not necessarily the teacher's UI language. */
  language?: string;
  /**
   * The ROUGH PLAN the main agent agreed with the teacher in chat, in the teacher's own terms: which
   * tasks, of which kind, worth how much.
   */
  confirmedPlan?: string;

  /** Anything the teacher said in chat that constrains the exam — emphases, exclusions, difficulty. */
  teacherNotes?: string;
  /** Source material the exam must stay inside. */
  groundingMaterial?: string;
}

export interface PlannedMaterial {
  id: string;
  kind: 'image' | 'text' | 'table' | 'grid' | 'plot';
  ref?: string;
  purpose: string;
  imagePrompt?: string;
  mustShow?: string[];
  aspect?: string;
  text?: FigureText;
  imageText?: string[];
  acceptance?: string;
}

export interface PlannedTask {
  number: string;
  taskType: string;
  points?: number;
  ownPoints?: number;
  focus: string;
  /**
   * Which answer region this task gets, in the same vocabulary the template and the structure layer use
   * (`AnswerType`). Anything else the model writes is replaced by the task type's own default — see
   * `normalizeAnswerSpace`.
   */
  answerSpace?: { kind: AnswerType; lines?: number; spaceMm?: number };
  materialIds?: string[];
}

export interface ExamPlan {
  title: string;
  subject: string;
  schoolClass: string;
  duration: string;
  totalPoints: number;
  lang: string;
  hints?: string;
  tasks: PlannedTask[];
  materials: PlannedMaterial[];
}

export interface ChangePlan {
  mode: 'patch' | 'full_replan';
  summary: string;
  newOrUpdatedMaterials: PlannedMaterial[];
  deletedMaterialIds?: string[];
  taskInstructions: string;
}

export interface ExamRefinementContext {
  currentSource: string;
  currentPlan: ExamPlan | null;
  instruction: string;
  existingImages?: Map<string, AcceptedImage>;
  existingPlots?: Map<string, AcceptedPlot>;
}

export interface ExamCreationOptions {
  signal?: AbortSignal;
  /** Pass when refining an existing exam. Runs the Delta Change Planner turn first. */
  refinement?: ExamRefinementContext;
}

// ─── the catalogue ────────────────────────────────────────────────────────────────────────────────

export function buildTaskTypeCatalogue(): string {
  const lines: string[] = [];
  for (const [type, description] of Object.entries(TaskTypeDescription)) {
    const behaviour = taskTypeBehaviours[type as TaskType];
    if (!behaviour.implemented) continue;
    lines.push(`- ${type}: ${description}`);
    const shapeRule = behaviour.examCreationPrompt.trim();
    if (shapeRule) lines.push(`    SHAPE: ${shapeRule}`);
  }
  return lines.join('\n');
}

/**
 * The template's function signatures, as the writer and editor agents are shown them.
 *
 * ONLY THE PUBLIC ONES. Roughly half of exam.typ's `#let`s are internal machinery — the metadata stream,
 * the text-extraction walk, the pieces `#task` builds its own answer region out of — and listing those
 * under "TEMPLATE FUNCTIONS AVAILABLE" invites a model to call them, which does not produce a badly styled
 * exam but a corrupt one (see `GRADIAS-PUBLIC-API` in exam.typ, which is the list). Read from the template
 * rather than repeated here for the same reason the signatures are: one file knows what it exposes.
 *
 * Falls back to every signature if that list cannot be read, because a prompt missing #task is a worse
 * failure than a prompt carrying some noise, and this runs on every generation.
 */
async function buildDialectReference(): Promise<string> {
  const template = await readTypstFile('exam.typ');
  // eslint-disable-next-line require-unicode-regexp -- the 'v' flag needs an es2024 target; project is es2020
  const signatures = new Map(
    [...template.matchAll(/^#let (?<name>[\w-]+)\((?<args>[^)]*)/gmu)].map(match => {
      // eslint-disable-next-line require-unicode-regexp -- see above
      const args = (match.groups?.args ?? '').replace(/\s+/gu, ' ').trim();
      return [match.groups?.name ?? '', `  ${match.groups?.name}(${args}${args.endsWith(',') ? ' …' : ''})`];
    }),
  );

  // eslint-disable-next-line require-unicode-regexp -- see above
  const declared = /^#let GRADIAS-PUBLIC-API = \((?<names>[^)]*)\)/mu.exec(template)?.groups?.names;
  // eslint-disable-next-line require-unicode-regexp -- see above
  const publicNames = [...(declared ?? '').matchAll(/"(?<name>[\w-]+)"/gu)].map(match => match.groups?.name ?? '');

  if (publicNames.length === 0) {
    logger.warn('exam-creation-agent: GRADIAS-PUBLIC-API not found in exam.typ — showing every signature');
    return [...signatures.values()].join('\n');
  }

  return publicNames
    .map(name => signatures.get(name))
    .filter((signature): signature is string => Boolean(signature))
    .join('\n');
}

// ─── prompts ────────────────────────────────────────────────────────────────────────────────────────

/**
 * The answer-region vocabulary, spelled out for the model.
 *
 * Shared by the planner and the task writer on purpose: the planner CHOOSES the type and the writer PASSES
 * IT THROUGH, and the two describing it differently is how they end up disagreeing. It names a default per
 * task type as well, because "which one for a calculation" was exactly the decision that kept defaulting to
 * ruled lines — squared paper existed and was never reached for.
 */
const ANSWER_SPACE_GUIDE = `ANSWER SPACE: EVERY task a student actually answers MUST declare one, and must
say WHICH KIND. This is not a default and not optional — a task with nowhere to write is rejected by the
structure check, because on paper the student has no room and online there is nothing to put an input into.
Pass it as "answer:" (and "answer-size:" when the default size is wrong).

  answer: "lines"           ruled writing lines. PROSE: explanations, interpretations, short answers,
                            essays, source analysis. "answer-size:" is the NUMBER OF LINES (default 8).
  answer: "numbered-lines"  ruled lines, each one numbered. For an answer that IS a numbered list: naming
                            the structures of a LABEL_IMAGE figure THAT HAS NO CALLOUTS, vocabulary pairs.
                            The printed number is what pairs the student's line with the master solution, so
                            it must be on the sheet. "answer-size:" is the number of lines — one per thing
                            to be named. A figure the material list gives you callouts for needs none of
                            this: its numbered boxes are already the lines, see "none" below.
  answer: "grid"            squared paper (Karopapier). CALCULATIONS, proofs, chemical equations, unit
                            conversions, term rearrangement — anything where digits, fractions, units and
                            columns must line up. Ruled lines are the WRONG choice here and blank space is
                            worse: neither gives a student anything to align against. "answer-size:" is a
                            height like 60mm.
  answer: "blank"           an empty box. DRAWINGS: sketches, diagrams, constructions, free-form figures.
                            "answer-size:" is a height like 70mm — a drawing needs more room than prose.
  answer: "inline"          the answer is written INSIDE the task body, so no space follows it: a #mc
                            checklist, a #gap-text(...) passage, #match-columns, a #grid-paper coordinate
                            system the student plots into, or a #prompt-list of vocabulary/short pairs. Say it
                            explicitly — "inline" means "the room is already in the body", which is a
                            different statement from leaving the argument out.
                            THE WIDGET MUST SIT INSIDE THIS TASK'S OWN BODY [...] — that is what ties it to
                            this task and tells the online exam page where to put the input. A #mc left
                            after the task's closing bracket prints identically and is rejected by the
                            structure check. Its id is not part of this: pass none and the template assigns
                            one.
  answer: "none"            for a task with no answer space of its OWN. Three cases, and only three: a
                            TASK_FRAME that GROUPS REAL SUBTASKS BELOW IT; a parent whose subtasks carry the
                            whole answer; and a task built on a #figure-block WITH CALLOUTS, whose numbered
                            boxes beside the picture are themselves the writing space (one per structure,
                            each on the end of a leader line) — that task adds no answer lines on top of
                            them. Anywhere else, if a task asks something a student has to actually work out
                            or write — even a short instruction like "analyse the following" or "compare the
                            two" — it is NOT a TASK_FRAME and it is NOT "none", no matter how much it reads
                            like an intro. A
                            TASK_FRAME with no subtasks under it is a task nobody will ever grade: its
                            points are permanently unearnable, which is a worse defect than choosing the
                            wrong answer type. When unsure whether something is a real question or framing
                            text, it is a real question — give it its own taskType and answer region.

Too little space is the most common way a generated exam fails in the room — when unsure, go bigger.`;

const CONTRACT = `HARD RULES (the document is machine-read; breaking these breaks grading):
1. Every task on the page MUST be declared with #task(id:, number:, type:, points:, ...). A task written
   as plain markup is invisible to the system: it gets no master solution, no grading scheme and no
   grading. There is no other way to create a task.
2. "number" carries the hierarchy: "3" is a main task, "3.a" a subtask of "3". A parent must appear
   before its subtasks. Numbers are how student answers are matched to tasks later, so they must be
   unique and in reading order.
3. "type" must be one of the task types listed below, spelled exactly.
4. Points: The plan gives you the exam's total points.
   You must distribute these points across your #task() declarations.
   CRITICAL RULE: Base the points on the reality of the material. If a LABEL_IMAGE
   material tells you it has exactly 9 callouts available, make that task worth 9 points.
   Distribute the remaining points logically among the other tasks (e.g. essays, math).
   A task's "points" is the total for its whole subtree. A parent whose subtasks carry points
   must equal their sum (add own-points: only if the parent ALSO asks something itself). The main tasks
   must sum to the exam's total-points.
5. Mathematics goes through #formula with the LaTeX in BACKTICKS: #formula(\`\\frac{a}{b}\`). Never write
   $...$ — inside this template that is typst math, not LaTeX, and \\frac in it is a compile error.
6. Multiple choice options go through #mc(...) INSIDE the task body. Options written as plain text are
   not part of the task and the grading agent never sees them.
7. Figures AND plots both go through #figure-block, with the root-relative path the material list gives
   you exactly (an image is "/assets/<id>.png", a plot is "/assets/<id>.svg" — use the extension shown,
   never assume one), placed INSIDE the body of the task that refers to them, before its answer space. Pass
   callouts: ONLY when the material list gives you the coordinates — never invent them. Whether a figure
   carries text of its own is already decided and stated in the material list: where it does, do not
   typeset that text again next to the figure; where it does not, every label belongs in your markup.
   If multiple small figures are needed, prefer to arrange them in a #layout-grid rather than as separate figures, so the layout is more compact and the student sees them together.
8. Do not redefine, wrap or shadow the template's functions, and do not add #show rules that change what
   they do.
9. A reading passage, dataset, table or source excerpt that more than one task depends on, or that stands
   apart from any single task, goes through #material(id:, title:, tasks: (...), body) — NOT typeset as
   plain markup — with "tasks:" listing every task number (as strings) that needs it. This is what makes
   it reach the master-solution, grading-scheme and grading steps later: content only one task uses can
   live directly inside that task's own body instead, exactly like ordinary prose.
10. A literal "[" or "]" printed as text (e.g. "answer in [brackets]", a footnote-style marker, an array
    written in prose) MUST be escaped as \\[ and \\] wherever it appears inside a body [...] block. Written
    unescaped, it is parsed as opening or closing that block instead of printing, which breaks the whole
    document.
11. Every #task a student answers MUST carry "answer:" naming the kind of space it gets (see ANSWER SPACE
    below for which kind). Omitting it is a structure error, not a stylistic choice: the task is rejected.
    Only a task with no space of its own — a TASK_FRAME THAT HAS REAL SUBTASKS BELOW IT, a parent whose
    subtasks carry the whole answer, or a task whose #figure-block has callouts (its numbered boxes ARE the
    writing space) — may use answer: "none". A TASK_FRAME with no subtasks is ALSO a structure error: it is
    a task nobody ever grades, so any points on it are unearnable. Do not use TASK_FRAME to avoid deciding
    a real answer type for a task that actually asks something.

DOCUMENT STRUCTURE VS. BLOCK CONTENT (CRITICAL):

1. TOP-LEVEL SKELETON (STRICT HOUSE MACROS ONLY):
   The outer document structure MUST ONLY consist of #task, #material, and #layout-grid calls.
   - NO untracked top-level text: section headings, instructions, or reading passages MUST go inside #material(id: "...", title: none)[...].
   - NO top-level native layout calls: to place tasks or materials side-by-side, ONLY use #layout-grid(columns: ...)[...]. Never use top-level native #grid.

2. INSIDE BLOCK CONTENT [...] (FREE NATIVE TYPST):
   Inside the content block [...] of any #task or #material, you have FULL freedom to use native Typst markup:
   - Mathematics: #formula(...)
   - Text formatting: *bold*, _italics_, lists, custom spacing, alignment.
   - Tables & helper macros: #table(...), #match-columns(...), #prompt-list(...), #data-table(...), #grid-paper(...).

3. ADDITIONAL HOUSE FUNCTIONS:
   These are the additional functions that need to be used in the exam:
   - multiple-choice options (#mc), its figure (#figure-block),
   a gap text's own passage (#gap-text), wrapping every individual gap inside it (#gap).

Otherwise the layout is yours. Try to be creative with the layout, but keep in mind that the main goal is to assess the students' knowledge and understanding of the subject matter.

${ANSWER_SPACE_GUIDE}`;

function planSystemPrompt(catalogue: string, hasConfirmedPlan: boolean): string {
  const planningBrief = hasConfirmedPlan
    ? `THE TEACHER HAS ALREADY AGREED A ROUGH PLAN, and it is given to you below. Your job is to turn it
into a complete plan, not to plan again:
- keep its tasks, their order, their subject as agreed
- fill in what it leaves open: the exact task type where none was named, sub-tasks where a task is
  clearly compound, the answer space, and the material each task needs
- only depart from it where it is internally impossible (a task type that cannot
  express what the task asks) and keep the change as small as possible

Silently rewriting what the teacher approved is the one failure mode that matters here: they will not see
it again until the exam is printed.`
    : `Decide: which tasks, in which order, of which task type and what material
(figures, source texts, tables, coordinate grids) each one needs.`;

  return `You are an experienced teacher planning a written exam. You produce a PLAN, not the exam itself.

${planningBrief}

Respect the requested total points and working time — a rule of thumb is roughly one point per minute of
working time, less for tasks that require drawing or long prose.

POINTS AND ANSWER SPACE ARE DECIDED HERE, NOT LATER. The writer downstream turns this plan into typst
directly, task by task, in the order you give — treat its "points" and "answerSpace" as fixed facts it is
not free to re-derive or second-guess. So THIS plan must leave nothing open:
- Every task's "points" is a concrete number, never omitted. A parent task's "points" equals the sum of its
  subtasks' "points" (plus its own "ownPoints" only if it ALSO asks something itself). The main (top-level)
  tasks' "points" must sum to exactly the exam's "totalPoints".
- Every task gets a concrete "answerSpace", never left out: {"kind": "<type>", "lines": n} for line types
  or {"kind": "<type>", "spaceMm": n} for box types. The types and what each is for are listed below —
  choose deliberately per task, this is the decision the writer downstream is not allowed to second-guess.
- TASK_FRAME IS ONLY FOR A NUMBER THAT HAS SUBTASKS UNDER IT IN THIS SAME PLAN (task "3" is a TASK_FRAME
  only if the plan also has tasks "3.a", "3.b", ...). The writer follows this plan's task list as given, not
  a rewritten one — so a TASK_FRAME planned with no subtasks is a task nobody will ever grade, and its
  points are unearnable by construction. If a number asks something a student has to work out or write, even
  a short "analyse the following" or "compare the two", give it the taskType that content actually is and
  split it into subtasks yourself if it is compound — never TASK_FRAME.
Getting this right here is what keeps the written exam matching this plan exactly.

${ANSWER_SPACE_GUIDE}

Cover the topic at several levels of demand: reproduction, application, and at least one task that asks
for reasoning or transfer. Do not make every task the same type.

TASK TYPES AVAILABLE (choose only from these, spelled exactly):
${catalogue}

MATERIAL. Decide it NOW, before the exam is written, because a figure that cannot be produced changes the
task that would have used it. For each figure state what must be visible; if a task asks students to
label it, list every structure to be labelled.

MATERIAL IS NOT ONLY FIGURES. "text", "table" and "grid" materials are shared context — a reading passage,
a dataset, a source table — that one or more tasks need to answer. List every task that depends on a
material in that task's own "materialIds" (a material three tasks share appears in all three tasks'
"materialIds"): that linkage is what the source phase later writes into the document, and what the
master-solution and grading agents read afterwards — a shared passage they cannot see is a passage they
cannot grade against.

CREATIVITY: In general try to be creative with the tasks and the material so that the exam not monotonous and interesting to the students.
Still, keep in mind the main goal is to assess the students' knowledge and understanding of the subject matter.

LANGUAGE SPLIT WITHIN THIS PLAN. "title", "subject", "hints" and every task's "focus" are the exam's own
content — write them in the exam's own language, whatever "lang" ends up being; that part is unchanged and
is what the source phase later turns into the printed document. "imagePrompt", "purpose" and "acceptance"
are never printed anywhere — they only talk to an image- or plot-generation model and to the vision model
that reviews the result, and those follow instructions more reliably in English than in a translated one.
Write those three ALWAYS IN ENGLISH, regardless of "lang". "mustShow" ("labels") is the one exception that
still has to stay in the exam's own language even though it also reaches those same models: for a task
that labels the figure it becomes the printed exam's master-solution key ("1 -> Aorta", not "1 -> atrium"),
graded against a student's answer written in that language — translating it here would silently break
grading. Do not fold "mustShow" into "imagePrompt" as a list to translate later ("must show: Vorhof,
Ventrikel, Aorta") — that reads as a caption for the image model to typeset, in whichever language it is
written. Instead write "imagePrompt" as one flowing, positively framed English description of the whole
illustration that already depicts everything "mustShow" names, in prose: an unlabelled cross-section
"showing the heart's two upper chambers, two lower chambers, and the wall between them" covers exactly what
"mustShow": ["Vorhof", "Ventrikel", "Herzscheidewand"] names, without handing the model a list.

TEXT INSIDE A FIGURE is your decision per figure, via "text":
- "none" — the default, and right for almost every diagram. The layout draws every label, number and
  caption, so text in the image only collides with it and cannot be corrected or translated.
- "labels" — text that belongs to the drawing and would be absurd outside it: axis labels with units,
  place names on a map, a legend. List the exact strings, IN THE EXAM'S OWN LANGUAGE (this is printed
  content, not an instruction), in "imageText".
- "verbatim" — the figure IS text-bearing material: a source excerpt, a newspaper clipping, a screenshot,
  a handwritten note to interpret. Put the text, in the exam's own language, in "imageText".
A figure a labelling task uses can never carry text — the numbers students write against are drawn on it.
Write "imagePrompt" to match: for a figure with "text": "none", describe an UNLABELLED drawing and never
ask for labels, a legend or a title. Image models copy the genre a prompt implies, and "labelled diagram
of X" is a request for lettering however firmly the rest of the prompt forbids it.
And note what image models are bad at: text they render is often misspelled and cannot be fixed
afterwards. If the text could just as well be typeset next to the figure, plan it as a "text" material
instead and let the document set it — that is always the better exam.

YOU REVIEW EVERY GENERATED FIGURE YOURSELF, against exactly what you write here and nothing else. So put
in "acceptance" what would make each figure usable for its task, in your own words and in English — that
is the standard you will be held to, and anything you leave out is something you cannot reject the image
for later.

A COORDINATE SYSTEM IS TWO DIFFERENT THINGS, and only one of them is material:
- if the student is meant to PLOT something themselves (sketch a graph, mark a point they compute), that
  is a BLANK grid — write it directly in the source with #grid-paper later; do not plan a material for it.
- if the coordinate system must ARRIVE with a function curve or specific points already on it — something
  the student reads, differentiates, interpolates, or annotates — plan a "kind": "plot" material. Put the
  exact function/domain/points in "imagePrompt", in English same as an image material (an approximate
  description is a wrong exam, not a rough one), and any point or curve that must be individually
  identifiable in "mustShow". A plot has no "text", "imageText" or "acceptance" — it is rendered exactly
  from what you write, not generated-then-judged.

Answer with JSON only (note which fields are English and which are "lang" below, per the language split above):
{"title": "...in the exam's own language...", "subject": "...", "schoolClass": "...",
 "duration": "45 Minuten", "totalPoints": 38, "lang": "de",
 "hints": "instructions printed under the header, in the exam's own language, or omit",
 "materials": [{"id": "m1", "kind": "image", "ref": "abb1", "purpose": "...always in English...",
                "imagePrompt": "...always in English, a flowing description, not a list...",
                "labels": ["Vorhof", "Ventrikel", "Herzscheidewand"], "aspect": "portrait", "text": "none",
                "imageText": ["only when text is wanted in the image — in the exam's own language"],
                "acceptance": "what makes this figure usable for its task — always in English"},
               {"id": "m2", "kind": "plot", "ref": "abb2", "purpose": "...always in English...",
                "imagePrompt": "the function/domain/points to plot, exactly — always in English",
                "labels": ["point A(2, 4)"]},
               {"id": "m3", "kind": "text",
                "purpose": "a shared reading passage two tasks both need — always in English; the passage itself is written in the source, in the exam's own language, not planned here"}],
 "tasks": [{"number": "1", "taskType": "ESSAY", "points": 12, "focus": "what this task asks, in the exam's own language",
            "answerSpace": {"kind": "lines", "lines": 12}, "materialIds": ["m3"]},
           {"number": "2", "taskType": "CALCULATION", "points": 10, "focus": "a task that is worked out, not written out",
            "answerSpace": {"kind": "grid", "spaceMm": 65}, "materialIds": []},
           {"number": "3", "taskType": "LABEL_IMAGE", "points": 6, "focus": "name the structures marked on the figure",
            "answerSpace": {"kind": "numbered-lines", "lines": 6}, "materialIds": ["m1"]},
           {"number": "4", "taskType": "MULTIPLE_CHOICE", "points": 2, "focus": "the options are the answer space",
            "answerSpace": {"kind": "inline"}, "materialIds": []},
           {"number": "5", "taskType": "TASK_FRAME", "points": 8, "focus": "short intro grouping the two calculations below — this text itself asks nothing",
            "answerSpace": {"kind": "none"}, "materialIds": []},
           {"number": "5.a", "taskType": "CALCULATION", "points": 4, "focus": "the first of the two short calculations the intro above refers to",
            "answerSpace": {"kind": "grid", "spaceMm": 40}, "materialIds": []},
           {"number": "5.b", "taskType": "CALCULATION", "points": 4, "focus": "the second short calculation",
            "answerSpace": {"kind": "grid", "spaceMm": 40}, "materialIds": []}]}
(every task carries a concrete "points" and "answerSpace" — never omit either. Note the five different
answer kinds above: they are chosen from what each task ASKS, not defaulted to lines. Task "5" is the ONLY
correct shape for TASK_FRAME: it is a real number IN THIS SAME "tasks" ARRAY with "5.a" and "5.b" following
it — a TASK_FRAME with no such entries is rejected, however introductory its text reads. The top-level
tasks' points, 12 + 10 + 6 + 2 + 8, sum to totalPoints 38 above — "5.a"/"5.b" are subtasks of "5" and are
not added again on top of it)`;
}

function changePlannerSystemPrompt(): string {
  return `You are an AI exam planner. You analyze an existing exam plan and a teacher's refinement request to produce a CHANGE PLAN (delta).

1. If the request requires a complete rewrite of topic/grade level or if no existing plan is available, set mode to "full_replan".
2. Otherwise, set mode to "patch" and specify ONLY the delta:
   - "newOrUpdatedMaterials": every new or modified material, each shaped EXACTLY like a material in a full
     plan (see the worked example below) — same field names, same types. A LABEL_IMAGE task whose material
     changes (a different subject, more/fewer structures to label) means the OLD image is now wrong and
     MUST be replaced here: give it "kind": "image" and a fresh "imagePrompt", or the old image is kept and
     the task ends up describing a picture that no longer matches it.
   - "deletedMaterialIds": IDs of materials no longer needed.
   - "taskInstructions": Clear instructions for the source writer detailing what tasks to add, modify, reword, or delete.

Reuse an existing material's own "id" when you are replacing what it shows — that is what tells the source
writer to regenerate the SAME figure slot rather than leave a second, orphaned one. Only invent a new id
for material that did not exist before.

Answer with JSON only:
{
  "mode": "patch" | "full_replan",
  "summary": "Short explanation",
  "newOrUpdatedMaterials": [{"id": "m1", "kind": "image", "ref": "abb1", "purpose": "...always in English...",
                             "imagePrompt": "...always in English, a flowing description, not a list...",
                             "mustShow": ["Bronchie", "Lungenflügel", "Luftröhre"], "aspect": "portrait",
                             "text": "none", "imageText": [],
                             "acceptance": "what makes this figure usable for its task — always in English"}],
  "deletedMaterialIds": [],
  "taskInstructions": "..."
}`;
}

const DIALECT_SYNTAX_NOTES = `SYNTAX FOR CALLS A SIGNATURE ALONE DOESN'T SHOW:

- ALWAYS start line 1 of the file by importing all template functions:
  #import "exam.typ": *
- Open the document with a show rule right after the import:
  #show: exam.with(title: "…", subject: "…", class: "…", duration: "…", total-points: …, lang: "de")
- #mc(options) — options is an array of content:
  #mc(([Text A], [Text B]))
- #data-table(header: (), rows: ())
- #match-columns(left: (), right: ()) — PUT THE RIGHT COLUMN IN A DIFFERENT ORDER than the left one.
  Written in the order they pair up, the sheet prints 1-A, 2-B, 3-C and the task answers itself.
- #prompt-list(items: ())
- #grid-paper(x-range: (), y-range: ())
- #layout-grid(columns: 2)[ #task(...) #figure-block(...) ]
- #material(id:, title:, tasks: (), body)

"id:" IS ONLY REQUIRED WHERE THE SIGNATURE SAYS SO — #task, #material and #figure-block. #mc,
#match-columns and #grid-paper take one too and you should NOT pass it: the template assigns those itself,
and which task such a widget belongs to is read off where it sits in the document, never off its name.
`;

function refineSourceSystemPrompt(catalogue: string, dialect: string): string {
  return `You update the Typst source code of a school exam according to a CHANGE PLAN.

${CONTRACT}

TASK TYPES (use exactly these spellings):
${catalogue}

TEMPLATE FUNCTIONS AVAILABLE — this is the whole list; anything else in "exam.typ" is internal and must
never be called:
${dialect}

${DIALECT_SYNTAX_NOTES}

REFINEMENT INSTRUCTIONS:
- Apply CHANGE PLAN to CURRENT Typst source.
- Keep unmodified tasks and materials byte-identical whenever possible.
- Keep total points, subtask sums, and the header #show: exam.with(total-points: ...) strictly synchronized.
- Ensure all text and layout remain wrapped inside house functions (#task, #material, #layout-grid).

Output ONLY the updated, complete Typst source code: no markdown fences, no commentary.`;
}

// ─── phase 1 helpers ─────────────────────────────────────────────────────────────────────────────

function describeRequest(request: ExamCreationRequest): string {
  const facts = [
    ['Subject', request.subject],
    ['Class', request.schoolClass],
    ['Topic', request.topic],
    ['Working time', request.durationMinutes ? `${request.durationMinutes} minutes` : undefined],
    ['Total points', request.totalPoints ? String(request.totalPoints) : undefined],
    ['Date', request.date],
    ['Teacher', request.teacher],
    ['School', request.school],
    ['Language of the exam', request.language],
  ]
    .filter((pair): pair is [string, string] => Boolean(pair[1]))
    .map(([label, value]) => `${label}: ${value}`);

  if (request.confirmedPlan) {
    facts.push(`ROUGH PLAN AGREED WITH THE TEACHER — refine this, do not replace it:\n${request.confirmedPlan}`);
  }
  if (request.teacherNotes) facts.push(`The teacher also said: ${request.teacherNotes}`);
  if (request.groundingMaterial) {
    facts.push(`MATERIAL THIS EXAM MUST STAY INSIDE:\n${request.groundingMaterial}`);
  }
  return facts.join('\n');
}

interface PlanViolation {
  taskNumber: string;
  message: string;
}

/**
 * The one plan defect that CANNOT be left for the compile-time repair loop to catch, because by the time
 * that loop runs it is too late to fix cheaply: a TASK_FRAME planned with no subtask under it in this same
 * plan. The writer follows this plan's task list as given — it has no way to invent the missing subtasks
 * after the fact. Left uncaught here, this exact plan would go all the way through material generation,
 * image generation and source writing — real cost — before the structure check in exam-structure.ts finally
 * rejects it and the whole thing has to be rewritten anyway.
 *
 * Every other plan defect (missing points, bad answerSpace, an unknown taskType) is already handled by
 * `normalizePlan`'s own coercions, which is why only this one needs a check here: it is the one case where
 * there is nothing to coerce TO without inventing content that is not this function's job to invent.
 */
function findPlanViolations(plan: ExamPlan): PlanViolation[] {
  const violations: PlanViolation[] = [];
  for (const task of plan.tasks) {
    if (task.taskType !== TaskType.TASK_FRAME) continue;
    const hasChildren = plan.tasks.some(other => other.number.startsWith(`${task.number}.`));
    if (hasChildren) continue;
    violations.push({
      taskNumber: task.number,
      message:
        `task "${task.number}" is TASK_FRAME (${task.points ?? 0} points) but the plan has no subtask ` +
        `numbered "${task.number}.a" or similar under it — TASK_FRAME is never graded on its own, so these ` +
        `points would be unearnable. Either add real subtasks under "${task.number}" with their own points ` +
        `and answerSpace, or give "${task.number}" itself the taskType its content actually is (its focus: ` +
        `"${task.focus}"), with a real answerSpace.`,
    });
  }
  return violations;
}

export async function requestExamPlan(examId: string, request: ExamCreationRequest): Promise<ExamPlan> {
  const router = getLLMRouter({}, 'batch');
  const messages: { role: 'system' | 'user' | 'assistant'; content: string }[] = [
    { role: 'system', content: planSystemPrompt(buildTaskTypeCatalogue(), Boolean(request.confirmedPlan)) },
    { role: 'user', content: describeRequest(request) },
  ];

  let plan: ExamPlan | undefined;
  let violations: PlanViolation[] = [];

  for (let round = 0; round <= MAX_PLAN_REPAIR_ROUNDS; round += 1) {
    if (violations.length > 0) {
      // The broken plan goes back in as the model's own turn, then the correction request as a new user
      // turn — the same shape `repairSource` uses, and for the same reason: showing what it wrote next to
      // what is wrong with it corrects one bad decision without re-deciding everything else in the plan.
      messages.push({ role: 'assistant', content: JSON.stringify(plan) });
      messages.push({
        role: 'user',
        content:
          `This plan cannot be handed to the writer as-is:\n${violations.map(v => `- ${v.message}`).join('\n')}\n\n` +
          `Return the CORRECTED full plan, same JSON shape, changing only what is broken.`,
      });
    }

    // eslint-disable-next-line no-await-in-loop -- each round corrects the previous one's plan
    const response = await router.chat.completions.create({
      stream: false,
      model: examCreationModel,
      provider: 'openrouter',
      temperature: examCreationTemperature,
      response_format: { type: 'json_object' } as any,
      messages,
      ...({ reasoning: { effort: examCreationReasoningEffort } } as any),
    });

    // eslint-disable-next-line no-await-in-loop -- cost must be attributed per call, not just the last one
    await addCostToExam(response, examId, 'examPlan');
    plan = normalizePlan(parseJson(response.choices[0]?.message.content), request);
    violations = findPlanViolations(plan);
    if (violations.length === 0) return plan;

    logger.info({ examId, round, violations: violations.map(v => v.taskNumber) }, 'Exam plan rejected, replanning');
  }

  // Ran out of repair rounds. Failing exam creation entirely over this would be a worse outcome than a
  // plan that is merely generic where it should have been precise — so every task still broken is
  // deterministically demoted to a real, gradable type instead of staying a childless TASK_FRAME.
  // DEFAULT is the right fallback specifically because it is not a domain decision: it says "read this
  // task's own focus/content to grade it", which is true regardless of what the content turns out to be.
  const stillBroken = new Set(violations.map(v => v.taskNumber));
  for (const task of plan!.tasks) {
    if (!stillBroken.has(task.number)) continue;
    logger.warn(
      { examId, taskNumber: task.number },
      'Demoting a childless TASK_FRAME to DEFAULT after exhausting plan repair rounds',
    );
    task.taskType = TaskType.DEFAULT;
    task.answerSpace = defaultAnswerSpace(TaskType.DEFAULT);
  }
  return plan!;
}

export async function requestChangePlan(examId: string, refinement: ExamRefinementContext): Promise<ChangePlan> {
  if (!refinement.currentPlan) {
    return { mode: 'full_replan', summary: 'No current plan found', newOrUpdatedMaterials: [], taskInstructions: '' };
  }

  const router = getLLMRouter({}, 'batch');
  const response = await router.chat.completions.create({
    stream: false,
    model: examCreationModel,
    provider: 'openrouter',
    temperature: 0,
    response_format: { type: 'json_object' } as any,
    messages: [
      { role: 'system', content: changePlannerSystemPrompt() },
      {
        role: 'user',
        content: `CURRENT PLAN:\n${describePlan(refinement.currentPlan)}\n\nTEACHER REQUEST:\n${refinement.instruction}`,
      },
    ],
  });

  await addCostToExam(response, examId, 'examChangePlanner');
  const parsed = parseJson(response.choices[0]?.message.content);
  return {
    mode: parsed?.mode === 'full_replan' ? 'full_replan' : 'patch',
    summary: String(parsed?.summary ?? ''),
    // Same freeform-JSON→typed-object gap `normalizePlan` closes for the main plan: without it, a material
    // whose "kind" isn't lowercased, or whose labels came back as "labels" instead of "mustShow", silently
    // fails `m.kind === 'image' && m.imagePrompt` in `runExamCreation`'s material phase — no image gets
    // generated, the old asset (wrong content) is kept, and `meetsCalloutMinimum` treats the missing
    // `mustShow` as "0 required" and reports stale callouts as available with nothing to back them.
    newOrUpdatedMaterials: (Array.isArray(parsed?.newOrUpdatedMaterials) ? parsed.newOrUpdatedMaterials : []).map(
      normalizeMaterial,
    ),
    deletedMaterialIds: Array.isArray(parsed?.deletedMaterialIds) ? parsed.deletedMaterialIds.map(String) : [],
    taskInstructions: String(parsed?.taskInstructions ?? refinement.instruction),
  };
}

function parseJson(raw: unknown): any {
  const text: string = typeof raw === 'string' ? raw : ((raw as any)?.content ?? '');
  if (!text.trim()) throw new Error('the model returned an empty plan');
  try {
    return JSON.parse(text);
  } catch {
    return JSON.parse(jsonrepair(text));
  }
}

const TASK_TYPES = new Set(Object.values(TaskType) as string[]);
const FIGURE_TEXT = new Set<string>(['none', 'labels', 'verbatim']);

function positiveOr(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && value > 0 ? value : fallback;
}

function normalizeMaterial(material: any, index: number): PlannedMaterial {
  return {
    id: String(material?.id ?? `m${index + 1}`),
    kind: ['image', 'text', 'table', 'grid', 'plot'].includes(String(material?.kind)) ? material.kind : 'text',
    ref: material?.ref ? String(material.ref) : undefined,
    purpose: String(material?.purpose ?? ''),
    imagePrompt: material?.imagePrompt ? String(material.imagePrompt) : undefined,
    mustShow: (Array.isArray(material?.mustShow) ? material.mustShow : (material?.labels ?? [])).map(String),
    aspect: material?.aspect ? String(material.aspect) : undefined,
    text: FIGURE_TEXT.has(String(material?.text)) ? (material.text as FigureText) : 'none',
    imageText: (Array.isArray(material?.imageText) ? material.imageText : []).map(String).filter(Boolean),
    acceptance: material?.acceptance ? String(material.acceptance) : undefined,
  };
}

/**
 * The main (top-level) number a task's hierarchical "number" belongs to: "3" for "3", "3" for "3.a". Used
 * to group a parent with its own subtasks so points can be reconciled within each group before the
 * cross-group total is checked against `totalPoints`.
 */
function mainNumberOf(task: PlannedTask): string {
  return task.number.split('.')[0] ?? task.number;
}

/**
 * Splits `available` points across `tasks` as evenly as whole points allow, handing any remainder to the
 * first tasks so the sum is exact. `available` is clamped to at least one point per task — a plan that left
 * points open should never produce a zero-point task.
 */
function distributeEvenly(tasks: PlannedTask[], available: number): void {
  if (tasks.length === 0) return;
  const total = Math.max(available, tasks.length);
  const base = Math.floor(total / tasks.length);
  const remainder = total - base * tasks.length;
  tasks.forEach((task, index) => {
    task.points = base + (index < remainder ? 1 : 0);
  });
}

/**
 * MANDATORY SAFETY NET, not a courtesy: relying on the writer to notice and silently correct inconsistent
 * points across the plan is a cross-task judgement call it should never have to make, so points must
 * already be final and consistent coming out of the plan, regardless of how well the model followed
 * `planSystemPrompt`'s instruction to fix them itself.
 *
 * Two passes:
 * 1. WITHIN each number group (a parent and its own subtasks, if any): fill any subtask missing "points" by
 *    distributing what's left of the parent's own "points" (after its "ownPoints" and its already-fixed
 *    subtasks are subtracted) across the ones still missing — then force the parent's "points" to
 *    `sum(subtasks) + ownPoints`, since the subtasks are the more specific signal once they're all fixed.
 * 2. ACROSS the top-level representatives (a parent, or a lone task with no subtasks): fill any still
 *    missing "points" by distributing what's left of `totalPoints` after every already-fixed representative
 *    is subtracted, then force-correct the last representative by whatever rounding difference remains so
 *    the sum is exactly `totalPoints`, never approximately.
 */
function reconcilePoints(tasks: PlannedTask[], totalPoints: number): PlannedTask[] {
  interface Group {
    root?: PlannedTask;
    subtasks: PlannedTask[];
  }
  const groups = new Map<string, Group>();
  const order: string[] = [];
  for (const task of tasks) {
    const main = mainNumberOf(task);
    let group = groups.get(main);
    if (!group) {
      group = { subtasks: [] };
      groups.set(main, group);
      order.push(main);
    }
    if (task.number === main) group.root = task;
    else group.subtasks.push(task);
  }

  for (const group of groups.values()) {
    if (group.subtasks.length === 0) continue;
    const missing = group.subtasks.filter(task => task.points === undefined);
    if (missing.length > 0) {
      const known = group.subtasks
        .filter(task => task.points !== undefined)
        .reduce((sum, task) => sum + (task.points ?? 0), 0);
      const ownPoints = group.root?.ownPoints ?? 0;
      const fromParent = group.root?.points !== undefined ? group.root.points - ownPoints - known : undefined;
      distributeEvenly(missing, fromParent ?? missing.length);
    }
    if (group.root) {
      group.root.points = group.subtasks.reduce((sum, task) => sum + (task.points ?? 0), 0) + (group.root.ownPoints ?? 0);
    }
  }

  const representatives = order
    .map(main => groups.get(main)!.root ?? groups.get(main)!.subtasks[0])
    .filter((task): task is PlannedTask => Boolean(task));

  const missingTop = representatives.filter(task => task.points === undefined);
  if (missingTop.length > 0) {
    const known = representatives
      .filter(task => task.points !== undefined)
      .reduce((sum, task) => sum + (task.points ?? 0), 0);
    distributeEvenly(missingTop, totalPoints - known);
  }

  const finalSum = representatives.reduce((sum, task) => sum + (task.points ?? 0), 0);
  const drift = totalPoints - finalSum;
  const last = representatives[representatives.length - 1];
  if (drift !== 0 && last) {
    last.points = Math.max(1, (last.points ?? 0) + drift);
    const group = groups.get(mainNumberOf(last));
    // Re-sum a parent whose own points we just nudged, so its subtask sum invariant still holds.
    if (group && group.root === last && group.subtasks.length > 0) {
      const ownPoints = last.ownPoints ?? 0;
      distributeEvenly(group.subtasks, last.points - ownPoints);
      last.points = group.subtasks.reduce((sum, task) => sum + (task.points ?? 0), 0) + ownPoints;
    }
  }

  return tasks;
}

/**
 * The answer region a task type gets when the plan does not say — the deterministic half of the same
 * decision `ANSWER_SPACE_GUIDE` asks the model to make.
 *
 * It exists because the old fallback was a flat "8 ruled lines for everything", which is why squared paper
 * was available for a year and never actually appeared: a model that forgets to choose got prose lines
 * under a calculation, and nothing downstream ever disagreed. Anything not listed falls through to
 * `lines`, which is the right default for prose and only for prose.
 */
const ANSWER_TYPE_BY_TASK_TYPE: Partial<Record<TaskType, AnswerType>> = {
  [TaskType.TASK_FRAME]: 'none',
  // The answer is printed inside the task body by #mc / #gap-text / #match-columns / #grid-paper / #prompt-list.
  [TaskType.MULTIPLE_CHOICE]: 'inline',
  [TaskType.GAP_TEXT]: 'inline',
  [TaskType.MAPPING]: 'inline',
  [TaskType.PLOTTING]: 'inline',
  // The vocabulary list IS the answer surface — see #prompt-list in exam.typ, built for exactly this: a
  // numbered "source -> ______" sheet keyed the same way translation.ts's own MS/GS format already is.
  [TaskType.TRANSLATION]: 'inline',
  // Numbered, because the number is what pairs the student's line with the master solution.
  [TaskType.LABEL_IMAGE]: 'numbered-lines',
  // Karopapier: digits, fractions, units and columns need something to line up against.
  [TaskType.CALCULATION]: 'grid',
  [TaskType.MATHEMATICAL_PROOF]: 'grid',
  [TaskType.CHEMICAL_FORMULA]: 'grid',
  [TaskType.CURVE_ANALYSIS]: 'grid',
  // Drawings, which need an open box and more of it than prose needs.
  [TaskType.SKETCH]: 'blank',
  [TaskType.DIAGRAM]: 'blank',
  [TaskType.GEOMETRY]: 'blank',
  [TaskType.ART]: 'blank',
  [TaskType.MUSIC]: 'blank',
};

const LINE_ANSWER_TYPES = new Set<AnswerType>(['lines', 'numbered-lines']);
const SIZELESS_ANSWER_TYPES = new Set<AnswerType>(['inline', 'none']);

/** Every task must leave the plan with a concrete answer space; this is the deterministic fallback. */
function defaultAnswerSpace(taskType: string): PlannedTask['answerSpace'] {
  const kind = ANSWER_TYPE_BY_TASK_TYPE[taskType as TaskType] ?? 'lines';
  if (SIZELESS_ANSWER_TYPES.has(kind)) return { kind };
  return LINE_ANSWER_TYPES.has(kind) ? { kind, lines: 8 } : { kind, spaceMm: 60 };
}

const ANSWER_TYPES = new Set<string>(['lines', 'numbered-lines', 'grid', 'blank', 'inline', 'none']);

/** Accepts what the model wrote and falls back to the task type's own default when it is unusable. */
function normalizeAnswerSpace(raw: any, taskType: string): PlannedTask['answerSpace'] {
  const kind = String(raw?.kind ?? '');
  if (!ANSWER_TYPES.has(kind)) return defaultAnswerSpace(taskType);

  const answerType = kind as AnswerType;
  if (SIZELESS_ANSWER_TYPES.has(answerType)) return { kind: answerType };

  if (LINE_ANSWER_TYPES.has(answerType)) {
    const lines = Number(raw?.lines);
    return { kind: answerType, lines: lines > 0 ? Math.round(lines) : 8 };
  }
  const spaceMm = Number(raw?.spaceMm);
  return { kind: answerType, spaceMm: spaceMm > 0 ? Math.round(spaceMm) : 60 };
}

function normalizePlan(parsed: any, request: ExamCreationRequest): ExamPlan {
  const tasks: PlannedTask[] = (Array.isArray(parsed?.tasks) ? parsed.tasks : [])
    .map((task: any) => ({
      number: String(task?.number ?? '').trim(),
      taskType: TASK_TYPES.has(String(task?.taskType)) ? String(task.taskType) : TaskType.DEFAULT,
      points: Number(task?.points) > 0 ? Number(task.points) : undefined,
      ownPoints: Number(task?.ownPoints) > 0 ? Number(task.ownPoints) : undefined,
      focus: String(task?.focus ?? '').trim(),
      answerSpace: normalizeAnswerSpace(
        task?.answerSpace,
        TASK_TYPES.has(String(task?.taskType)) ? String(task.taskType) : TaskType.DEFAULT,
      ),
      materialIds: Array.isArray(task?.materialIds) ? task.materialIds.map(String) : [],
    }))
    .filter((task: PlannedTask) => task.number.length > 0);

  if (tasks.length === 0) throw new Error('the plan contains no tasks');

  const materials: PlannedMaterial[] = (Array.isArray(parsed?.materials) ? parsed.materials : [])
    .map(normalizeMaterial)
    .filter((material: PlannedMaterial) => material.id.length > 0);

  const totalPoints = positiveOr(
    Number(parsed?.totalPoints),
    positiveOr(
      request.totalPoints,
      tasks.reduce((sum, task) => sum + (task.points ?? 0), 0),
    ),
  );

  reconcilePoints(tasks, totalPoints);

  return {
    title: String(parsed?.title ?? `${request.subject ?? 'Klausur'} — ${request.topic ?? ''}`).trim(),
    subject: String(parsed?.subject ?? request.subject ?? ''),
    schoolClass: String(parsed?.schoolClass ?? request.schoolClass ?? ''),
    duration: String(parsed?.duration ?? (request.durationMinutes ? `${request.durationMinutes} Minuten` : '')),
    totalPoints,
    lang: String(parsed?.lang ?? request.language ?? 'de'),
    hints: parsed?.hints ? String(parsed.hints) : undefined,
    tasks,
    materials,
  };
}

// ─── phase 3 helpers ───────────────────────────────────────────────────────────────────────────────

function tasksUsing(plan: ExamPlan, materialId: string): PlannedTask[] {
  return plan.tasks.filter(task => task.materialIds?.includes(materialId));
}

function figureModeFor(plan: ExamPlan, materialId: string): FigureMode {
  return tasksUsing(plan, materialId).some(task => task.taskType === TaskType.LABEL_IMAGE) ? 'labelled' : 'reference';
}

function describeFigureContext(plan: ExamPlan, material: PlannedMaterial): string {
  const usedBy = tasksUsing(plan, material.id);
  const lines = [
    `Exam: ${plan.title} — ${plan.subject}, class ${plan.schoolClass}, ${plan.duration}, language ${plan.lang}`,
  ];
  if (material.purpose) lines.push(`Purpose of this figure: ${material.purpose}`);
  lines.push(
    usedBy.length > 0
      ? `Used by:\n${usedBy.map(task => `- task ${task.number} [${task.taskType}]: ${task.focus}`).join('\n')}`
      : 'No task references it yet — it is general material for the exam.',
  );
  return lines.join('\n');
}

interface MaterialOutcomes {
  images: Map<string, AcceptedImage>;
  rejectedImages: Map<string, string>;
  plots: Map<string, AcceptedPlot>;
  rejectedPlots: Map<string, string>;
}

function describeOneMaterial(plan: ExamPlan, material: PlannedMaterial, outcomes: MaterialOutcomes): string {
  if (material.kind === 'plot') {
    return describePlotMaterial(material, outcomes.plots, outcomes.rejectedPlots);
  }

  const image = outcomes.images.get(material.id);
  if (image) {
    const lines = [
      `- ${material.id} (image${material.ref ? `, reference "${material.ref}"` : ''}): AVAILABLE at ` +
        `/assets/${material.id}.${extensionForImage(image.mimeType)}`,
    ];
    if (material.purpose) lines.push(`  purpose: ${material.purpose}`);
    if (meetsCalloutMinimum(image.callouts, material.mustShow?.length ?? 0)) {
      lines.push(
        `  callouts AVAILABLE — pass them to #figure-block exactly as: callouts: (${(image.callouts ?? [])
          .map(callout => `(label: "${callout.label}", x: ${callout.x.toFixed(3)}, y: ${callout.y.toFixed(3)})`)
          .join(', ')})`,
      );
      // The numbered boxes #figure-block draws are themselves writing space, one per structure, each at
      // the end of a leader line pointing at what it names — so a task using them needs no answer region
      // of its own, and printing one anyway leaves the student a second, unnumbered place to write the
      // same list. Said here rather than in the plan, because whether callouts exist is only known once
      // the figure has actually been generated, which happens after planning.
      lines.push(
        `  those numbered boxes ARE the answer space for the task that uses this figure — give that task` +
          ` answer: "none" and add no answer lines of its own, whatever the plan said.`,
      );
    } else if (figureModeFor(plan, material.id) === 'labelled') {
      lines.push(`  callouts NOT available for this figure. Ask students to name structures in a list.`);
    } else {
      lines.push('  reference material only — pass no callouts.');
    }
    return lines.join('\n');
  }

  const reason = outcomes.rejectedImages.get(material.id);
  if (reason) {
    return `- ${material.id}: NOT AVAILABLE (${reason}). Rewrite task to work WITHOUT a figure.`;
  }
  return describeSharedMaterial(plan, material);
}

function describeMaterials(plan: ExamPlan, outcomes: MaterialOutcomes): string {
  if (plan.materials.length === 0) return 'No material was planned.';
  return plan.materials.map(material => describeOneMaterial(plan, material, outcomes)).join('\n');
}

function describeSharedMaterial(plan: ExamPlan, material: PlannedMaterial): string {
  const usedBy = tasksUsing(plan, material.id).map(task => task.number);
  const linkage =
    usedBy.length === 0
      ? 'no task references it yet.'
      : `used by tasks ${usedBy.join(', ')} — write through #material(id: "${material.id}", tasks: (${usedBy.map(n => `"${n}"`).join(', ')}), ...)`;
  return `- ${material.id} (${material.kind}): ${material.purpose} — build this yourself in the source; ${linkage}`;
}

function describePlotMaterial(
  material: PlannedMaterial,
  accepted: Map<string, AcceptedPlot>,
  rejected: Map<string, string>,
): string {
  if (accepted.has(material.id)) {
    return `- ${material.id} (plot): AVAILABLE at /assets/${material.id}.svg — reference material only.`;
  }
  const reason = rejected.get(material.id);
  if (reason) {
    return `- ${material.id}: NOT AVAILABLE (${reason}). Rewrite task without a plot.`;
  }
  return `- ${material.id} (plot): ${material.purpose} — build this yourself in the source.`;
}

/**
 * Renders the planned answer space as the typst arguments that produce it, rather than as prose.
 *
 * The writer's only job with this decision is to pass it through, so handing it "12 ruled lines" and hoping
 * it translates that into `answer: "lines", answer-size: 12` adds a step that can go wrong for nothing.
 */
function describeAnswerSpace(space: PlannedTask['answerSpace']): string {
  const kind = space?.kind;
  if (!kind) return 'answer: "lines", answer-size: 8';
  if (kind === 'inline') {
    return (
      `answer: "inline" — whichever of #mc(...)/#gap-text(...)/#match-columns(...)/#grid-paper(...)/` +
      `#prompt-list(...) carries this task's answer MUST sit inside this task's own body [...], and needs ` +
      `no id of its own`
    );
  }
  if (kind === 'none') return 'answer: "none" (this task is not answered itself)';
  if (kind === 'lines' || kind === 'numbered-lines') {
    return `answer: "${kind}", answer-size: ${space?.lines ?? 8}`;
  }
  return `answer: "${kind}", answer-size: ${space?.spaceMm ?? 60}mm`;
}

function describePlan(plan: ExamPlan): string {
  const tasks = plan.tasks
    .map(task => {
      const spaceText = describeAnswerSpace(task.answerSpace);
      const pointsText = task.points ? `${task.points} points` : 'points not yet decided';
      return (
        `- Task ${task.number} [${task.taskType}] ${pointsText}` +
        `${task.ownPoints ? ` (own: ${task.ownPoints})` : ''}, ${spaceText}` +
        `${task.materialIds?.length ? `, uses material ${task.materialIds.join(', ')}` : ''}\n  Focus: ${task.focus}`
      );
    })
    .join('\n');

  const materials =
    plan.materials.length > 0
      ? plan.materials
          .map(mat => {
            const details: string[] = [`- Material ${mat.id} [${mat.kind}]${mat.ref ? ` (ref: "${mat.ref}")` : ''}:`];
            if (mat.purpose) details.push(`  Purpose: ${mat.purpose}`);
            if (mat.imagePrompt) {
              details.push(
                mat.kind === 'plot'
                  ? `  Plot Spec/Code: ${mat.imagePrompt}`
                  : `  Image Description: ${mat.imagePrompt}`,
              );
            }
            if (mat.mustShow?.length) details.push(`  Must Show/Labels: ${mat.mustShow.join(', ')}`);
            if (mat.text && mat.text !== 'none') details.push(`  Text Mode: ${mat.text}`);
            return details.join('\n');
          })
          .join('\n')
      : 'None';

  return `EXAM
Title: ${plan.title}
Subject: ${plan.subject}   Class: ${plan.schoolClass}   Duration: ${plan.duration}
Total points: ${plan.totalPoints}   Language: ${plan.lang}
${plan.hints ? `Hints: ${plan.hints}` : ''}

MATERIALS
${materials}

TASKS
${tasks}`;
}

// ─── phase 3: deterministic header ────────────────────────────────────────────────────────────────

/**
 * The `#import`/`#show: exam.with(...)` header, built directly from the plan — no LLM call. Everything it
 * needs (title, subject, class, duration, total-points, lang, hints, and the request's own date/teacher/
 * school) is already known after PHASE 1, so writing it ourselves removes an entire class of repair-loop
 * failures caused by an LLM-authored header (wrong argument name, wrong total, mismatched hyphenation) and
 * means the source call below never has to be taught header syntax at all — see `sourceSystemPrompt`,
 * which tells it not to write one.
 */
function buildExamHeader(plan: ExamPlan, request: ExamCreationRequest): string {
  const args: string[] = [
    `title: ${formatTypstString(plan.title)}`,
    `subject: ${formatTypstString(plan.subject)}`,
    `class: ${formatTypstString(plan.schoolClass)}`,
    `duration: ${formatTypstString(plan.duration)}`,
    `total-points: ${plan.totalPoints}`,
    `lang: ${formatTypstString(plan.lang)}`,
  ];
  if (plan.hints) args.push(`hints: ${formatTypstString(plan.hints)}`);
  if (request.date) args.push(`date: ${formatTypstString(request.date)}`);
  if (request.teacher) args.push(`teacher: ${formatTypstString(request.teacher)}`);
  if (request.school) args.push(`school: ${formatTypstString(request.school)}`);
  return `#import "exam.typ": *\n#show: exam.with(${args.join(', ')})`;
}

function sourceSystemPrompt(catalogue: string, dialect: string): string {
  return `You write the typst source of a school exam, using the house template — not its header: someone
else already wrote the "#import"/"#show" line from the plan, exactly once, before your output. Do not write
it again; start straight with the first #material(...) or #task(...).

${CONTRACT}

TASK TYPES (use exactly these spellings):
${catalogue}

TEMPLATE FUNCTIONS AVAILABLE (import what you use from "exam.typ"):
${dialect}

${DIALECT_SYNTAX_NOTES}

Write the exam in the language given by the plan's "lang". Output ONLY the typst source for the materials
and tasks: no header, no markdown fences, no commentary before or after it.`;
}

function buildCreationUserPrompt(plan: ExamPlan, materialsDescription: string, request: ExamCreationRequest): string {
  return [
    describePlan(plan),
    '',
    'MATERIAL',
    materialsDescription,
    request.teacherNotes ? `\nTHE TEACHER ASKED FOR:\n${request.teacherNotes}` : '',
    request.groundingMaterial
      ? `\nSTAY INSIDE THIS MATERIAL — do not introduce facts or notation absent from it:\n${request.groundingMaterial}`
      : '',
  ].join('\n');
}

/**
 * How many tasks the source written so far declares. `#task(...)` is the one thing every task must go
 * through (see WHAT THE MODEL IS AND IS NOT FREE TO DO at the top of this file), which is what makes
 * counting them a real measure of progress rather than a guess at one.
 */
function countTasksInSource(source: string): number {
  // eslint-disable-next-line require-unicode-regexp -- the 'v' flag needs an es2024 target; project is es2020
  return (source.match(/#task\s*\(/gu) ?? []).length;
}

/**
 * The source call, STREAMED so the tasks can be counted as they are written.
 *
 * WHY STREAM SOMETHING NOBODY READS TOKEN BY TOKEN. This one model call writes the whole document — every
 * task at once — so with `stream: false` there is no moment between "nothing exists" and "all N tasks
 * exist", and therefore no honest per-task progress to report. Streaming turns exactly that into an
 * observable sequence: each `#task(` that arrives is one more task that genuinely exists now.
 *
 * The accumulated text is identical to what the non-streamed call returned (same `stripFences` on the
 * whole thing at the end), so nothing downstream can tell the difference.
 *
 * COST STILL COUNTS: `include_usage` asks for the usage-bearing final chunk, and whichever chunk carries
 * it is what `addCostToExam` is given. If a provider sends none, cost tracking for this call degrades to
 * zero rather than throwing (see `addCostToExam`).
 */
async function streamExamSource(
  examId: string,
  messages: Array<{ role: 'system' | 'user'; content: string }>,
  costLabel: string,
  onTasksWritten?: (count: number) => void,
): Promise<string> {
  const router = getLLMRouter({}, 'batch');
  const response = await router.chat.completions.create({
    stream: true,
    model: examCreationModel,
    provider: 'openrouter',
    temperature: examCreationTemperature,
    messages,
    // Not part of the chat-completions type, forwarded verbatim by the router — same mechanism the image
    // model's `modalities` uses in exam-images.ts.
    ...({ stream_options: { include_usage: true } } as any),
  } as any);

  let content = '';
  let reported = 0;
  let usageCarrier: any = null;

  for await (const chunk of response as any) {
    if (chunk?.usage) usageCarrier = chunk;

    const delta = chunk?.choices?.[0]?.delta?.content ?? '';
    if (!delta) continue;
    content += delta;

    // Only on an actual increase, so this writes at most once per task rather than once per token.
    const written = countTasksInSource(content);
    if (written > reported) {
      reported = written;
      onTasksWritten?.(written);
    }
  }

  await addCostToExam(usageCarrier ?? {}, examId, costLabel);
  return stripFences(content);
}

async function writeSource(
  examId: string,
  userPrompt: string,
  onTasksWritten?: (count: number) => void,
): Promise<string> {
  const dialect = await buildDialectReference();
  return streamExamSource(
    examId,
    [
      { role: 'system', content: sourceSystemPrompt(buildTaskTypeCatalogue(), dialect) },
      { role: 'user', content: userPrompt },
    ],
    'examSource',
    onTasksWritten,
  );
}

async function writeRefinedSource(
  examId: string,
  refinement: {
    currentSource: string;
    changePlan: ChangePlan;
    updatedPlan: ExamPlan;
    materialsDescription: string;
  },
  onTasksWritten?: (count: number) => void,
): Promise<string> {
  const { currentSource, changePlan, updatedPlan, materialsDescription } = refinement;
  const dialect = await buildDialectReference();

  const userPrompt = [
    `CURRENT SOURCE:\n${currentSource}`,
    '',
    `CHANGE PLAN SUMMARY:\n${changePlan.summary}`,
    `TASK INSTRUCTIONS:\n${changePlan.taskInstructions}`,
    '',
    describePlan(updatedPlan),
    '',
    'AVAILABLE MATERIAL:',
    materialsDescription,
  ].join('\n');

  // A refinement rewrites the whole document too, so its tasks stream in exactly the same way.
  return streamExamSource(
    examId,
    [
      { role: 'system', content: refineSourceSystemPrompt(buildTaskTypeCatalogue(), dialect) },
      { role: 'user', content: userPrompt },
    ],
    'examRefineSource',
    onTasksWritten,
  );
}

function stripFences(source: string): string {
  // eslint-disable-next-line require-unicode-regexp -- the 'v' flag needs an es2024 target; project is es2020
  const fenced = /^\s*```(?:typst|typ)?\n(?<body>[\s\S]*?)```\s*$/u.exec(source);
  return (fenced?.groups?.body ?? source).trim();
}

// ─── phase 4: repair & check ──────────────────────────────────────────────────────────────────────

function findTopLevelViolations(source: string): string[] {
  const problems: string[] = [];
  // eslint-disable-next-line require-unicode-regexp -- see stripFences above
  const clean = source.replace(/\/\/[^\n]*/g, '').replace(/\/\*[\s\S]*?\*\//g, '');
  let i = 0;

  while (i < clean.length) {
    // eslint-disable-next-line require-unicode-regexp -- see stripFences above
    while (i < clean.length && /\s/.test(clean.charAt(i))) i += 1;
    if (i >= clean.length) break;

    if (clean.charAt(i) === '#') {
      const rest = clean.slice(i);
      // eslint-disable-next-line require-unicode-regexp -- see stripFences above
      const importMatch = /^#import\s+[^\n]+/u.exec(rest);
      // eslint-disable-next-line require-unicode-regexp -- see stripFences above
      const showMatch = /^#show\b:?\s*exam\b/u.exec(rest);
      // eslint-disable-next-line require-unicode-regexp -- see stripFences above
      const houseMatch = /^#(?:task|material|layout-grid)\b/u.exec(rest);

      if (importMatch) {
        i += importMatch[0].length;
        continue;
      }

      if (showMatch || houseMatch) {
        i = skipCallSpan(clean, i);
        continue;
      }

      // eslint-disable-next-line require-unicode-regexp -- see stripFences above
      const unknownCall = /^#[\w-]+/u.exec(rest)?.[0] ?? '#unknown';
      problems.push(
        `unapproved top-level call "${unknownCall}" — top-level layout must use #layout-grid, #task, or #material`,
      );
      i = skipCallSpan(clean, i);
    } else {
      const snippet = clean
        .slice(i, i + 40)
        // eslint-disable-next-line require-unicode-regexp -- see stripFences above
        .replace(/\n/g, ' ')
        .trim();
      problems.push(
        `untracked top-level text found ("${snippet}...") — wrap all text in #material(id: "...", title: none)[...] or #task`,
      );
      const nextHash = clean.indexOf('#', i);
      i = nextHash === -1 ? clean.length : nextHash;
    }
  }

  return problems;
}

function skipCallSpan(str: string, start: number): number {
  let i = start;
  while (i < str.length && str.charAt(i) !== '(' && str.charAt(i) !== '\n') i += 1;
  if (i < str.length && str.charAt(i) === '(') {
    let depth = 1;
    i += 1;
    while (i < str.length && depth > 0) {
      if (str.charAt(i) === '(') depth += 1;
      else if (str.charAt(i) === ')') depth -= 1;
      i += 1;
    }
  }
  // eslint-disable-next-line require-unicode-regexp -- see stripFences above
  while (i < str.length && /\s/.test(str.charAt(i))) i += 1;
  if (i < str.length && str.charAt(i) === '[') {
    let depth = 1;
    i += 1;
    while (i < str.length && depth > 0) {
      if (str.charAt(i) === '[') depth += 1;
      else if (str.charAt(i) === ']') depth -= 1;
      i += 1;
    }
  }
  return i;
}

async function repairSource(examId: string, plan: ExamPlan, source: string, diagnostics: string): Promise<string> {
  const router = getLLMRouter({}, 'batch');
  const response = await router.chat.completions.create({
    stream: false,
    model: examCreationModel,
    provider: 'openrouter',
    temperature: examRepairTemperature,
    messages: [
      {
        role: 'system',
        content: `You fix a typst exam source that failed validation or compilation.

${CONTRACT}

THIS IS A TARGETED FIX, NOT A REWRITE. Change only what the diagnostics below require. Every "#task(...)"
and "#material(...)" the diagnostics do not mention must reappear BYTE-FOR-BYTE identical — copy it, do not
retype it from memory. Rewriting the whole document from scratch, even with good intentions, is exactly how
a task having nothing to do with the diagnostics quietly disappears: it is checked afterwards by number, and
losing even one fails this repair regardless of whether the flagged problem got fixed.

Output ONLY the corrected, complete typst source. No fences, no commentary.`,
      },
      {
        role: 'user',
        content: `THE PLAN THIS EXAM WAS WRITTEN FROM:\n${describePlan(plan)}\n\nDIAGNOSTICS\n${diagnostics}\n\nSOURCE\n${source}`,
      },
    ],
  });

  await addCostToExam(response, examId, 'examRepair');
  return stripFences(response.choices[0]?.message.content ?? '');
}

/**
 * Every `#task(...)`'s "number:" value anywhere in `source` — used to catch a repair round that "fixed" its
 * flagged diagnostic by quietly deleting an unrelated task instead (see the PHASE 4 loop). Deliberately a
 * raw-text scan, not `encodeExamDocument`: that parser only splits ONE lexical level, so a task nested
 * inside a top-level `#layout-grid(...)` — exactly the wrapper CONTRACT rule 1 tells the writer to use for
 * side-by-side tasks — would stay hidden inside an opaque `raw` node and read as "missing" even when it is
 * still there. Same reasoning `countTasksInSource` already uses to just count these instead of parsing them.
 */
function extractTaskNumbers(source: string): Set<string> {
  const numbers = new Set<string>();
  // eslint-disable-next-line require-unicode-regexp -- matches the project's es2020 target, see stripFences
  const taskCallPattern = /#task\(/gu;
  let match: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign -- the idiomatic way to drive a global regex's exec loop
  while ((match = taskCallPattern.exec(source))) {
    // Depth-aware, like `skipCallSpan`: stops at THIS call's own closing paren, not a nested one, so a
    // `number:` belonging to some other call never gets attributed to this one.
    let depth = 1;
    let i = match.index + match[0].length;
    while (i < source.length && depth > 0) {
      if (source.charAt(i) === '(') depth += 1;
      else if (source.charAt(i) === ')') depth -= 1;
      i += 1;
    }
    const head = source.slice(match.index + match[0].length, i - 1);
    // eslint-disable-next-line require-unicode-regexp -- see above
    const numberMatch = /number\s*:\s*"([^"]*)"/u.exec(head);
    if (numberMatch?.[1]) numbers.add(numberMatch[1]);
  }
  return numbers;
}

async function checkSource(source: string, assets: ExamAsset[]): Promise<string | null> {
  const violations = findTopLevelViolations(source);

  try {
    const result = await compileExamSource(source, assets);
    const errors = result.problems
      .filter(problem => problem.severity === 'error')
      .map(problem => `- ${problem.message}`);
    const allDiagnostics = [...violations.map(v => `- ${v}`), ...errors];

    if (allDiagnostics.length === 0) return null;
    return allDiagnostics.join('\n');
  } catch (err) {
    if (err instanceof TypstCompileError) {
      return violations.length > 0
        ? `${violations.map(v => `- ${v}`).join('\n')}\n${err.diagnostics}`
        : err.diagnostics;
    }
    throw err;
  }
}

// ─── unified execution ────────────────────────────────────────────────────────────────────────────

export interface ExamCreationOutcome extends PersistResult {
  plan: ExamPlan;
  repairRounds: number;
  rejectedMaterials: string[];
}

/**
 * Phase 1 of `runExamCreation`, pulled out so `plan`/`changePlan` are each assigned exactly once — a
 * refinement's own reassignment from "the change plan" to "null, we replanned instead" happened in the
 * same scope `runExamCreation` reads both from, which is exactly the shape `require-atomic-updates` warns
 * about (a value read, then reassigned, then read again). Returning a fresh pair instead means the caller
 * only ever sees the FINAL decision, never an intermediate one.
 *
 * Falls back to a full replan (rather than a non-null assertion on `refinement.currentPlan`) if the change
 * planner somehow returns "patch" without a current plan to patch — `requestChangePlan` never actually
 * does this (it forces `full_replan` itself when there is no current plan), but nothing here needs to
 * assume that invariant holds forever to stay safe.
 */
async function determinePlanForRun(
  examId: string,
  request: ExamCreationRequest,
  refinement: ExamRefinementContext | undefined,
): Promise<{ plan: ExamPlan; changePlan: ChangePlan | null }> {
  if (!refinement) {
    return { plan: await requestExamPlan(examId, request), changePlan: null };
  }

  const changePlan = await requestChangePlan(examId, refinement);
  logger.info({ examId, mode: changePlan.mode, summary: changePlan.summary }, 'Change plan evaluated');

  const currentPlan = refinement.currentPlan;
  if (changePlan.mode === 'full_replan' || !currentPlan) {
    // The tool call that triggers a refinement deliberately omits subject/topic/confirmedPlan (see
    // create-exam.tool.ts) because the ordinary refinement path never needs them — it patches the stored
    // plan instead of inventing one. A full replan here means that plan was missing (an exam created
    // before `examPlanJson` existed, or one whose stored JSON failed to parse), so `request` alone is just
    // the teacher's one-line edit with nothing to plan from. Grounding the replan in the exam's own current
    // source gives the model real content to work from instead of empty tasks.
    const plan = await requestExamPlan(examId, {
      ...request,
      teacherNotes: `${request.teacherNotes ?? ''}\nRefinement request: ${refinement.instruction}`.trim(),
      groundingMaterial: [request.groundingMaterial, `CURRENT EXAM SOURCE:\n${refinement.currentSource}`]
        .filter(Boolean)
        .join('\n\n'),
    });
    return { plan, changePlan: null };
  }

  const deletedIds = new Set(changePlan.deletedMaterialIds ?? []);
  const updatedIds = new Set(changePlan.newOrUpdatedMaterials.map(material => material.id));
  const plan: ExamPlan = {
    ...currentPlan,
    materials: [
      ...currentPlan.materials.filter(material => !deletedIds.has(material.id) && !updatedIds.has(material.id)),
      ...changePlan.newOrUpdatedMaterials,
    ],
  };
  return { plan, changePlan };
}

/**
 * Creates or refines an exam end to end and persists it.
 */
export async function runExamCreation(
  examId: string,
  request: ExamCreationRequest,
  options: ExamCreationOptions = {},
): Promise<ExamCreationOutcome> {
  const images = new Map<string, AcceptedImage>(options.refinement?.existingImages ?? []);
  const plots = new Map<string, AcceptedPlot>(options.refinement?.existingPlots ?? []);
  const rejectedImages = new Map<string, string>();
  const rejectedPlots = new Map<string, string>();

  // ── PHASE 1: PLAN (or CHANGE PLAN) ──
  // No sub-progress: a plan is one call (plus, rarely, a couple of `findPlanViolations` repair rounds), so
  // there is nothing granular worth reporting — the ring just sits at this phase's own band (see
  // `examCreatorPercent` in chat-input.tsx) until the plan comes back.
  await setExamCreationPhaseByExam(examId, ExamCreationPhase.PLAN).catch(err =>
    logger.warn({ err, examId }, 'Failed to set examCreationState phase to PLAN'),
  );
  const { plan, changePlan } = await determinePlanForRun(examId, request, options.refinement);

  // No patch to apply — either a plain creation, or a refinement that fell back to a full replan — means
  // any material carried over from a previous run is stale: the plan replacing it doesn't reference it by
  // id any more, so keeping it around would just leave an unused image/plot staged for no reason.
  if (changePlan) {
    for (const id of changePlan.deletedMaterialIds ?? []) {
      images.delete(id);
      plots.delete(id);
    }
  } else {
    images.clear();
    plots.clear();
  }

  logger.info({ examId, tasks: plan.tasks.length, materials: plan.materials.length }, 'Exam plan ready');

  // THE SOURCE PHASE'S OWN DENOMINATOR, known as soon as the plan comes back: how many tasks this run will
  // write. Not set on the exam yet — that happens when PHASE 3 actually starts (see `setExamCreationPhaseByExam`
  // below) — this closure is only defined here because it closes over `plan.tasks.length`.
  const plannedTaskCount = plan.tasks.length;
  let tasksWritten = 0;
  const reportTasksWritten = (count: number): Promise<void> => {
    // Monotonic and capped: the bar must not walk backwards, and a model that writes more tasks than it
    // planned must not push the ring past full.
    const next = Math.min(Math.max(count, tasksWritten), plannedTaskCount);
    if (next === tasksWritten) return Promise.resolve();
    tasksWritten = next;
    // Never rethrown: a progress write that fails must not fail the creation it is only describing.
    return setExamCreationFinishedByExam(examId, next).catch(err =>
      logger.warn({ err, examId, tasks: next }, 'Failed to update exam-creation task progress'),
    );
  };

  // ── PHASE 2: MATERIAL ──
  const targetMaterials = changePlan ? changePlan.newOrUpdatedMaterials : plan.materials;

  const imageRequests: ImageRequest[] = targetMaterials
    .filter(m => m.kind === 'image' && m.imagePrompt)
    .map(m => ({
      id: m.id,
      prompt: m.imagePrompt ?? '',
      mode: figureModeFor(plan, m.id),
      mustShow: m.mustShow ?? [],
      aspect: m.aspect,
      text: m.text ?? 'none',
      imageText: m.imageText ?? [],
      acceptance: m.acceptance,
      context: describeFigureContext(plan, m),
    }));

  const plotRequests: PlotRequest[] = targetMaterials
    .filter(m => m.kind === 'plot' && m.imagePrompt)
    .map(m => ({
      id: m.id,
      spec: m.imagePrompt ?? '',
      mustShow: m.mustShow ?? [],
      context: describeFigureContext(plan, m),
    }));

  // Every image/plot is generated (and reviewed/compiled, with retries) independently of every other one,
  // so — unlike SOURCE's tasks, which the reverted per-task fan-out showed the downside of — there is no
  // coordination lost by reporting each as it lands rather than waiting for the whole batch.
  await setExamCreationPhaseByExam(examId, ExamCreationPhase.MATERIAL, imageRequests.length + plotRequests.length).catch(
    err => logger.warn({ err, examId }, 'Failed to set examCreationState phase to MATERIAL'),
  );
  let materialsFinished = 0;
  const reportMaterialFinished = (): void => {
    materialsFinished += 1;
    setExamCreationFinishedByExam(examId, materialsFinished).catch(err =>
      logger.warn({ err, examId, materialsFinished }, 'Failed to update exam-creation material progress'),
    );
  };

  const emptyImages: ImageOutcome = { accepted: [], rejected: [] };
  const emptyPlots: PlotOutcome = { accepted: [], rejected: [] };

  const [newImages, newPlots] = await Promise.all([
    imageRequests.length > 0
      ? prepareExamImages(examId, imageRequests, { signal: options.signal, onItemFinished: reportMaterialFinished })
      : emptyImages,
    plotRequests.length > 0
      ? preparePlots(examId, plotRequests, { signal: options.signal, onItemFinished: reportMaterialFinished })
      : emptyPlots,
  ]);

  newImages.accepted.forEach(img => {
    images.set(img.id, img);
  });
  newImages.rejected.forEach(img => {
    rejectedImages.set(img.id, img.reason);
  });
  newPlots.accepted.forEach(p => {
    plots.set(p.id, p);
  });
  newPlots.rejected.forEach(p => {
    rejectedPlots.set(p.id, p.reason);
  });

  const assets: ExamAsset[] = [
    ...Array.from(images.values()).map(img => ({
      name: `${img.id}.${extensionForImage(img.mimeType)}`,
      bytes: img.bytes,
    })),
    ...Array.from(plots.values()).map(p => ({ name: `${p.id}.svg`, bytes: p.svg })),
  ];

  const materialOutcomes: MaterialOutcomes = { images, rejectedImages, plots, rejectedPlots };
  const materialsDescription = describeMaterials(plan, materialOutcomes);

  // ── PHASE 3: SOURCE ──
  // Both paths write the whole document as ONE streamed call — the contract for a refinement is to keep
  // untouched tasks byte-identical, which only a call that sees the whole current source can do, and a
  // fresh creation gets the same treatment for consistency and simplicity. `reportTasksWritten` moves the
  // ring from a `#task(` occurrence count as the stream arrives (see `streamExamSource`).
  await setExamCreationPhaseByExam(examId, ExamCreationPhase.SOURCE, plannedTaskCount).catch(err =>
    logger.warn({ err, examId }, 'Failed to set examCreationState phase to SOURCE'),
  );
  let source: string;
  if (changePlan && options.refinement) {
    source = await writeRefinedSource(
      examId,
      {
        currentSource: options.refinement.currentSource,
        changePlan,
        updatedPlan: plan,
        materialsDescription,
      },
      reportTasksWritten,
    );
  } else {
    // FRESH (OR FULLY REPLANNED) CREATION: the header is built deterministically from the plan (see
    // `buildExamHeader`) rather than left to the model, which removes an entire class of repair-loop
    // failures (wrong argument name, wrong total, mismatched hyphenation) — the model only ever writes the
    // materials and tasks that follow it.
    const body = await writeSource(
      examId,
      buildCreationUserPrompt(plan, materialsDescription, request),
      reportTasksWritten,
    );
    source = [buildExamHeader(plan, request), body].join('\n\n');
  }
  // The document is written, so every task this run produces exists now — including the case where the
  // model wrote fewer than it planned and the stream count stopped short of the denominator. What is left
  // (repair rounds, the compile, the persist) corrects that document rather than adding tasks to it.
  await reportTasksWritten(plannedTaskCount);

  // ── PHASE 4: REPAIR & PERSIST ──
  // `total` is MAX_REPAIR_ROUNDS, not "rounds actually needed" — unknowable in advance, and the ring
  // should read as "however far through the repair budget we are", not claim a false 100% the moment a
  // source that needed no repair at all reaches this phase. Never rethrown — same convention as every
  // other progress write in this function.
  await setExamCreationPhaseByExam(examId, ExamCreationPhase.REPAIR, MAX_REPAIR_ROUNDS).catch(err =>
    logger.warn({ err, examId }, 'Failed to set examCreationState phase to REPAIR'),
  );

  // The authoritative "what must exist" set — every task the PLAN calls for, not just what happens to
  // survive the first write — so whichever of them disappears from here on can only be a repair round's doing.
  const expectedTaskNumbers = new Set(plan.tasks.map(task => task.number));

  let diagnostics = await checkSource(source, assets);
  let repairRounds = 0;
  while (diagnostics && repairRounds < MAX_REPAIR_ROUNDS) {
    repairRounds += 1;
    const thisRound = repairRounds;
    logger.info({ examId, repairRounds, diagnostics }, 'Repairing generated exam source');
    // Never rethrown — a progress write failing must not fail the repair round it is only describing.
    setExamCreationFinishedByExam(examId, thisRound).catch(err =>
      logger.warn({ err, examId, repairRound: thisRound }, 'Failed to update exam-creation repair progress'),
    );
    // eslint-disable-next-line no-await-in-loop -- each round corrects the previous one's output
    const repaired = await repairSource(examId, plan, source, diagnostics);

    // A repair call rewrites the WHOLE document, and can "fix" its flagged diagnostic by quietly deleting
    // a task that had nothing to do with it — plausible-looking output, since a task that no longer exists
    // cannot fail a check about its own answer region either. Nothing about a SMALLER document looks wrong
    // on its own, which is exactly why this is checked by number rather than trusted from a clean `checkSource`.
    const survivingNumbers = extractTaskNumbers(repaired);
    const dropped = [...expectedTaskNumbers].filter(number => !survivingNumbers.has(number));
    if (dropped.length > 0) {
      logger.warn({ examId, repairRounds, dropped }, 'Repair round silently deleted tasks — discarding it, retrying');
      // The REJECTED round's own output is never assigned to `source` — the next attempt starts from the
      // same, still-broken-but-complete source as this one did, with the deletion named alongside the
      // original diagnostics so both get fixed together instead of trading one defect for another.
      diagnostics =
        `${diagnostics}\n\nYOUR LAST CORRECTION ALSO SILENTLY DELETED these tasks, which must still exist: ` +
        `${dropped.join(', ')}. Fix ONLY what is named above — every other task, including these, must ` +
        `reappear exactly as it was before.`;
      continue;
    }

    source = repaired;
    // eslint-disable-next-line no-await-in-loop -- and is then re-checked
    diagnostics = await checkSource(source, assets);
  }

  if (diagnostics) {
    throw new Error(`Exam source still failing after ${MAX_REPAIR_ROUNDS} repairs:\n${diagnostics}`);
  }

  const figureAssets = Array.from(images.values()).map(img => ({ materialId: img.id, fileId: img.fileId }));
  const plotAssets = Array.from(plots.values()).map(p => ({ materialId: p.id, code: p.code }));
  const persisted = await compileAndPersistExam(
    examId,
    source,
    assets,
    figureAssets,
    plotAssets,
    JSON.stringify(plan),
    () => {
      setExamCreationPdfPersistedByExam(examId).catch(err =>
        logger.warn({ err, examId }, 'Failed to set exam-creation PDF-persisted checkpoint'),
      );
    },
  );

  return {
    ...persisted,
    plan,
    repairRounds,
    rejectedMaterials: [...Array.from(rejectedImages.keys()), ...Array.from(rejectedPlots.keys())],
  };
}

// ─── refinement context ─────────────────────────────────────────────────────────────────────────────

/**
 * Assembles what `runExamCreation`'s `refinement` option needs from an exam that already has a source —
 * the seam between "the job has an examId and an instruction" and "the agent has everything it needs to
 * patch, not regenerate, this exam". Returns `null` when there is nothing to refine (no source yet), in
 * which case the caller should fall back to a plain (non-refinement) creation run.
 *
 * IMAGES AND PLOTS ARE RECONSTRUCTED, not merely referenced: `runExamCreation` stages every material's
 * bytes as a compile-time shadow asset regardless of whether this run touches it (typst still has to
 * resolve `/assets/<id>.*` for the ones that don't), and re-persists `figureAssets`/`plotAssets` wholesale
 * on every run (see compile-exam.ts) — so a material left out here would silently vanish from a patch that
 * never meant to touch it. A plot's bytes are regenerated from its stored code (see `Exam.plotAssets`'s own
 * doc comment on why that, not a stored SVG, is what persists); an image's bytes are read back from its
 * `ImageFile`, same as the editor's own `loadPersistedAssets` in exam-editor-api.ts.
 *
 * CALLOUTS COME FROM THE CURRENT SOURCE, not from the original generation attempt: a teacher may have
 * dragged them in the exam editor since, and the source — not `figureAssets`, which carries no callout
 * data at all — is the one place that reflects whatever was last saved.
 */
export async function loadRefinementContext(
  examId: string,
  instruction: string,
): Promise<ExamRefinementContext | null> {
  const { getCollection } = await import('@adornis/baseql/server/collections.js');
  const { getByID } = await import('@adornis/baseql/operations/mongo.js');
  const { ImageFile } = await import('@adornis/file-utils/db/images.js');
  const { readFile } = await import('node:fs/promises');
  const { Exam } = await import('../../exam.js');
  const { encodeExamDocument, getArg, NESTED_TASK_CALLS, parseCallouts, parseTypstString } =
    await import('./exam-document.js');

  const examCollection = await getCollection(Exam);
  const exam = await examCollection.findOne({ _id: examId });
  const currentSource = String((exam as any)?.examSource ?? '');
  if (!currentSource) return null;

  let currentPlan: ExamPlan | null = null;
  try {
    const raw = (exam as any)?.examPlanJson;
    currentPlan = raw ? (JSON.parse(raw) as ExamPlan) : null;
  } catch (err) {
    logger.warn({ err, examId }, 'Stored exam plan is not valid JSON, refining without one');
  }

  const calloutsByMaterialId = new Map<string, Callout[]>();
  for (const node of encodeExamDocument(currentSource)) {
    if (node.type !== 'call' || (node.name !== 'task' && node.name !== 'material')) continue;
    for (const child of encodeExamDocument(node.body ?? '', NESTED_TASK_CALLS)) {
      if (child.type !== 'call' || child.name !== 'figure-block') continue;
      const materialId = parseTypstString(getArg(child, 'id') ?? '');
      const rawCallouts = getArg(child, 'callouts');
      if (!materialId || !rawCallouts) continue;
      const parsed = parseCallouts(rawCallouts);
      if (parsed.length > 0) calloutsByMaterialId.set(materialId, parsed);
    }
  }

  const figureAssets: { materialId: string; fileId: string }[] = Array.isArray((exam as any)?.figureAssets)
    ? (exam as any).figureAssets
    : [];
  const existingImages = new Map<string, AcceptedImage>();
  for (const { materialId, fileId } of figureAssets) {
    // eslint-disable-next-line no-await-in-loop -- sequential reads of a handful of already-persisted figures
    const imageFile = await getByID(ImageFile, fileId)({ meta: { extension: 1 } } as any);
    const extension = String((imageFile as any)?.meta?.extension ?? '');
    if (!imageFile || !extension) continue;
    // eslint-disable-next-line no-await-in-loop -- see above
    const bytes = await readFile(imageFile.getFullPath());
    existingImages.set(materialId, {
      id: materialId,
      bytes: Buffer.from(bytes),
      // Only ever fed back into `extensionForImage`, which is what needs to land on `extension` again —
      // the real mime type was never persisted, and nothing here needs it to mean anything more than that.
      mimeType: `image/${extension === 'jpg' ? 'jpeg' : extension}`,
      fileId,
      review: {
        verdict: 'accept',
        usable: true,
        critique: '',
        containsText: false,
        textCorrect: null,
        legibleInGreyscale: true,
        missing: [],
      },
      callouts: calloutsByMaterialId.get(materialId) ?? null,
      attempts: 1,
    });
  }

  const plotAssets: { materialId: string; code: string }[] = Array.isArray((exam as any)?.plotAssets)
    ? (exam as any).plotAssets
    : [];
  const existingPlots = new Map<string, AcceptedPlot>();
  for (const { materialId, code } of plotAssets) {
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential recompiles of a handful of already-verified plots
      const svg = await compileStandaloneSvg(code);
      existingPlots.set(materialId, { id: materialId, svg: Buffer.from(svg, 'utf8'), code, attempts: 1 });
    } catch (err) {
      logger.error({ err, examId, materialId }, 'Stored plot code no longer compiles, dropping it from refinement');
    }
  }

  return { currentSource, currentPlan, instruction, existingImages, existingPlots };
}
